import type {
  IntakeScopeCandidate,
  IntakeState,
  IntakeWorkItem,
} from "../structured-intake-model.js";

export function StructuredIntakeCards({
  classificationBusy,
  intakeActionBusy,
  intakeMode,
  intakeState,
  jobOfferings,
  onAcceptStandardDimension,
  onConfirmClassification,
  onConfirmMeasurement,
  onResolveScope,
  onDecideFact,
  onDecideObservation,
  onUploadCapture,
}: {
  classificationBusy: string | null;
  intakeActionBusy: string | null;
  intakeMode: boolean;
  intakeState: IntakeState | null;
  jobOfferings: Array<{ code: string; name: string }>;
  onAcceptStandardDimension: (candidateId: string) => Promise<void>;
  onConfirmClassification: (item: IntakeWorkItem, projectType: string) => Promise<void>;
  onConfirmMeasurement: (measurementId: string) => Promise<void>;
  onResolveScope: (
    candidate: IntakeScopeCandidate,
    resolution: "accept" | "reject" | "correct",
    projectType?: string,
  ) => Promise<void>;
  onDecideFact: (factId: string, decision: "confirm" | "reject") => Promise<void>;
  onDecideObservation: (observationId: string, decision: "confirm" | "reject") => Promise<void>;
  onUploadCapture: (
    task: NonNullable<IntakeState["captureTasks"]>[number],
    file: File,
  ) => Promise<void>;
}) {
  return (
    <>
      {intakeMode && (intakeState?.workItems.length || intakeState?.scopeCandidates?.length) ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="font-bold text-emerald-950">Detected project scopes</p>
          <p className="mt-1 text-sm text-emerald-900/70">
            Here’s what we understood. You can correct a possible extra scope; it will not affect
            your estimate unless you choose it.
          </p>
          <div className="mt-3 space-y-3">
            {intakeState.workItems.map((item) => (
              <div
                className="flex flex-col gap-2 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                key={item.id}
              >
                <div>
                  <p className="font-semibold capitalize">
                    {item.canonical_type.replaceAll("_", " ")}
                  </p>
                  <p className="text-xs text-stone-500">
                    {item.confidence != null
                      ? `${Math.round(Number(item.confidence) * 100)}% classification confidence`
                      : "Classification needs clarification"}
                  </p>
                </div>
                {["confirmed", "collecting", "ready", "converted"].includes(item.status) ? (
                  <span className="text-sm font-bold text-emerald-800">Included</span>
                ) : (
                  <div className="flex gap-2">
                    <select
                      aria-label={`Correct ${item.canonical_type} project type`}
                      className="rounded-lg border border-stone-300 px-2 py-1.5 text-sm"
                      defaultValue={item.canonical_type}
                      disabled={classificationBusy === item.id}
                      onChange={(event) => void onConfirmClassification(item, event.target.value)}
                    >
                      {jobOfferings.map((offering) => (
                        <option key={offering.code} value={offering.code}>
                          {offering.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                      disabled={classificationBusy === item.id}
                      onClick={() => void onConfirmClassification(item, item.canonical_type)}
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                )}
              </div>
            ))}
            {intakeState.scopeCandidates
              ?.filter((candidate) => candidate.status === "pending")
              .map((candidate) => (
                <div
                  className="flex flex-col gap-2 rounded-xl border border-dashed border-stone-300 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                  key={candidate.id}
                >
                  <div>
                    <p className="font-semibold capitalize">
                      Possible: {candidate.canonical_type.replaceAll("_", " ")}
                    </p>
                    <p className="text-xs text-stone-500">This is not included in your estimate.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm font-bold"
                      disabled={classificationBusy === candidate.id}
                      onClick={() => void onResolveScope(candidate, "reject")}
                      type="button"
                    >
                      Not needed
                    </button>
                    <button
                      className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                      disabled={classificationBusy === candidate.id}
                      onClick={() => void onResolveScope(candidate, "accept")}
                      type="button"
                    >
                      Include
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}
      {intakeMode && intakeState?.facts?.some((fact) => fact.status === "proposed") ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="font-bold text-amber-950">Confirm project details</p>
          <p className="mt-1 text-sm text-amber-900/70">
            These details remain proposals until you confirm them. Assumptions are clearly labelled.
          </p>
          <div className="mt-3 space-y-2">
            {intakeState.facts
              .filter((fact) => fact.status === "proposed")
              .map((fact) => (
                <div
                  className="flex flex-col gap-2 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                  key={fact.id}
                >
                  <div>
                    <p className="font-semibold capitalize">
                      {fact.field.replaceAll("_", " ")}
                      {fact.assumption ? " (assumption)" : ""}
                    </p>
                    <p className="text-sm text-stone-600">
                      {String(fact.normalized_value ?? fact.raw_value)}
                      {fact.unit ? ` ${fact.unit}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm font-bold"
                      disabled={intakeActionBusy === fact.id}
                      onClick={() => void onDecideFact(fact.id, "reject")}
                      type="button"
                    >
                      Correct
                    </button>
                    <button
                      className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                      disabled={intakeActionBusy === fact.id}
                      onClick={() => void onDecideFact(fact.id, "confirm")}
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}
      {intakeMode &&
      intakeState?.measurements?.some((measurement) =>
        ["proposed", "provisional"].includes(measurement.status),
      ) ? (
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
          <p className="font-bold text-sky-950">Review measurements</p>
          <div className="mt-3 space-y-2">
            {intakeState.measurements
              .filter((measurement) => ["proposed", "provisional"].includes(measurement.status))
              .map((measurement) => (
                <div
                  className="flex items-center justify-between gap-3 rounded-xl bg-white p-3"
                  key={measurement.id}
                >
                  <div>
                    <p className="font-semibold capitalize">
                      {measurement.measurement_type.replaceAll("_", " ")}
                    </p>
                    <p className="text-sm text-stone-600">
                      {measurement.raw_value} {measurement.raw_unit}
                      {measurement.normalized_value != null
                        ? ` · normalized ${measurement.normalized_value} ${measurement.normalized_unit}`
                        : ""}
                    </p>
                    {measurement.validation?.warnings?.map((warning) => (
                      <p className="mt-1 text-xs font-semibold text-amber-700" key={warning}>
                        {warning}
                      </p>
                    ))}
                  </div>
                  <button
                    className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                    disabled={
                      intakeActionBusy === measurement.id ||
                      measurement.validation?.blocking === true
                    }
                    onClick={() => void onConfirmMeasurement(measurement.id)}
                    type="button"
                  >
                    Confirm
                  </button>
                </div>
              ))}
          </div>
        </div>
      ) : null}
      {intakeMode && intakeState?.standardDimensionCandidates?.length ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="font-bold text-amber-950">Possible standard sizes</p>
          <p className="mt-1 text-sm text-amber-900/75">
            These are planning approximations matched against this contractor’s catalog. Physical
            verification is still required before ordering.
          </p>
          <div className="mt-3 space-y-2">
            {intakeState.standardDimensionCandidates.map((candidate) => (
              <div
                className="flex items-center justify-between gap-3 rounded-xl bg-white p-3"
                key={candidate.id}
              >
                <div>
                  <p className="font-semibold">
                    {candidate.nominal_dimensions.width} × {candidate.nominal_dimensions.height}{" "}
                    {candidate.unit}
                  </p>
                  <p className="text-sm text-stone-600">
                    Raw: {candidate.raw_dimensions.width} × {candidate.raw_dimensions.height}{" "}
                    {candidate.unit}
                  </p>
                </div>
                <button
                  className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                  disabled={intakeActionBusy === candidate.id}
                  onClick={() => void onAcceptStandardDimension(candidate.id)}
                  type="button"
                >
                  Use for planning
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {intakeMode &&
      intakeState?.observations?.some((observation) => observation.status === "proposed") ? (
        <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50 p-4">
          <p className="font-bold text-fuchsia-950">Review visible photo details</p>
          <p className="mt-1 text-sm text-fuchsia-900/70">
            These are model-proposed observations, not confirmed project facts.
          </p>
          <div className="mt-3 space-y-2">
            {intakeState.observations
              .filter((observation) => observation.status === "proposed")
              .map((observation) => (
                <div
                  className="flex flex-col gap-2 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                  key={observation.id}
                >
                  <div>
                    <p className="font-semibold capitalize">
                      {observation.field.replaceAll("_", " ")}
                    </p>
                    <p className="text-sm text-stone-600">
                      {String(observation.proposed_value)} ·{" "}
                      {Math.round(Number(observation.semantic_confidence) * 100)}% confidence
                    </p>
                    {observation.explanation ? (
                      <p className="mt-1 text-xs text-stone-500">{observation.explanation}</p>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm font-bold"
                      disabled={intakeActionBusy === observation.id}
                      onClick={() => void onDecideObservation(observation.id, "reject")}
                      type="button"
                    >
                      Reject
                    </button>
                    <button
                      className="rounded-lg bg-emerald-800 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                      disabled={intakeActionBusy === observation.id}
                      onClick={() => void onDecideObservation(observation.id, "confirm")}
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}
      {intakeMode && intakeState?.captureTasks?.some((task) => task.status === "pending") ? (
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <p className="font-bold text-violet-950">Requested project evidence</p>
          <div className="mt-3 space-y-2">
            {intakeState.captureTasks
              .filter((task) => task.status === "pending")
              .map((task) => (
                <label className="block cursor-pointer rounded-xl bg-white p-3" key={task.id}>
                  <span className="font-semibold">{task.instruction}</span>
                  <span className="mt-2 block text-sm font-bold text-violet-800">
                    {intakeActionBusy === task.id ? "Uploading…" : "Choose photo"}
                  </span>
                  <input
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    disabled={intakeActionBusy === task.id}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void onUploadCapture(task, file);
                    }}
                    type="file"
                  />
                </label>
              ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
